import { useState, useEffect } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { useTranslation, useI18nStore } from '../lib/i18n';
import { useAuthStore } from '../lib/auth';
import { useDomain, canRegister } from '../lib/domain';
import { useSystemSettings } from '../hooks/useSystemSettings';
import { useTheme } from '../hooks/useTheme';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Calendar, Eye, EyeOff, Loader2, Globe, Moon, Sun } from 'lucide-react';
import { toast } from 'sonner';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';

const LANGUAGES = [
  { code: 'tr', name: 'Türkçe', flag: '🇹🇷' },
  { code: 'en', name: 'English', flag: '🇬🇧' },
  { code: 'nl', name: 'Nederlands', flag: '🇳🇱' },
  { code: 'fr', name: 'Français', flag: '🇫🇷' }
];

export const Login = () => {
  const { t } = useTranslation();
  const { language, setLanguage } = useI18nStore();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { login, logout, isLoading, setToken, fetchUser } = useAuthStore();
  const { settings } = useSystemSettings();
  const { theme, toggleTheme } = useTheme();
  const domainConfig = useDomain();
  
  const [showPassword, setShowPassword] = useState(false);
  const [formData, setFormData] = useState({
    email: '',
    password: ''
  });

  // Handle impersonation token from URL
  useEffect(() => {
    const impersonateToken = searchParams.get('impersonate');
    if (!impersonateToken) return;
    
    // Save token to auth storage directly (zustand persist format)
    // Note: storage key is 'hr-auth-storage' not 'auth-storage'
    const authState = {
      state: {
        user: null,
        accessToken: impersonateToken,
        refreshToken: null,
        isAuthenticated: true,
        isLoading: false,
        error: null
      },
      version: 0
    };
    localStorage.setItem('hr-auth-storage', JSON.stringify(authState));
    
    // Redirect with full page load - zustand will read from localStorage on fresh load
    window.location.replace('/dashboard');
  }, [searchParams]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    const result = await login(formData.email, formData.password);
    
    if (result.success) {
      toast.success(t('auth.loginSuccess'));
      navigate('/dashboard');
    } else {
      toast.error(result.error);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 dark:bg-slate-900 flex" data-testid="login-page">
      {/* Left Side - Form */}
      <div className="flex-1 flex items-center justify-center p-8">
        <div className="w-full max-w-md">
          {/* Language Selector & Theme Toggle */}
          <div className="flex justify-end gap-2 mb-4">
            <Button 
              variant="outline" 
              size="sm" 
              onClick={toggleTheme}
              data-testid="login-theme-toggle"
            >
              {theme === 'light' ? (
                <Moon className="h-4 w-4" />
              ) : (
                <Sun className="h-4 w-4" />
              )}
            </Button>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline" size="sm" className="gap-2">
                  <Globe className="h-4 w-4" />
                  {LANGUAGES.find(lang => lang.code === language)?.flag}
                  {LANGUAGES.find(lang => lang.code === language)?.name}
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                {LANGUAGES.map((lang) => (
                  <DropdownMenuItem
                    key={lang.code}
                    onClick={() => setLanguage(lang.code)}
                    className={language === lang.code ? 'bg-slate-100 dark:bg-slate-800' : ''}
                  >
                    <span className="mr-2">{lang.flag}</span>
                    {lang.name}
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>
          </div>

          {/* Firma Domain Header - Logo ve Başlık */}
          {!domainConfig.isMainDomain && domainConfig.firmId && (
            <div className="mb-8 p-6 bg-gradient-to-r from-slate-800 to-slate-700 rounded-2xl text-white">
              <div className="flex items-center gap-4">
                {domainConfig.logoUrl ? (
                  <img 
                    src={domainConfig.logoUrl} 
                    alt={domainConfig.firmName || 'Logo'} 
                    className="h-14 w-auto bg-white dark:bg-slate-800 rounded-lg p-2"
                  />
                ) : (
                  <div className="h-14 w-14 bg-white dark:bg-slate-800/20 rounded-lg flex items-center justify-center">
                    <Calendar className="h-8 w-8 text-white" />
                  </div>
                )}
                <div>
                  <h2 className="text-xl font-bold">
                    {domainConfig.siteTitle || domainConfig.firmName || 'İK Portalı'}
                  </h2>
                  {domainConfig.siteDescription && (
                    <p className="text-slate-300 text-sm mt-1">{domainConfig.siteDescription}</p>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Ana Domain Logo */}
          {domainConfig.isMainDomain && (
            <Link to="/" className="flex items-center gap-2 mb-8">
              <Calendar className="h-8 w-8 text-slate-800 dark:text-slate-100" />
              <span className="font-bold text-xl text-slate-800 dark:text-slate-100" style={{ fontFamily: 'Manrope, sans-serif' }}>
                {settings.siteName || 'OMS Portal'}
              </span>
            </Link>
          )}

          {/* Form */}
          <div className="bg-white dark:bg-slate-800 rounded-2xl shadow-sm border border-slate-100 dark:border-slate-700 p-8">
            <h1 
              className="text-2xl font-bold text-slate-900 dark:text-slate-100 mb-2"
              style={{ fontFamily: 'Manrope, sans-serif' }}
            >
              {t('auth.loginTitle')}
            </h1>
            <p className="text-slate-500 dark:text-slate-400 mb-8">
              {/* Kayıt linki sadece izin veriliyorsa göster */}
              {canRegister(domainConfig) ? (
                <>
                  {t('auth.noAccount')}{' '}
                  <Link to="/register" className="text-slate-900 dark:text-slate-100 font-medium hover:underline">
                    {t('common.register')}
                  </Link>
                </>
              ) : (
                <span>{t('auth.welcomeBack') || 'Hesabınıza giriş yapın'}</span>
              )}
            </p>

            <form onSubmit={handleSubmit} className="space-y-5">
              <div className="space-y-2">
                <Label htmlFor="email" className="text-slate-700 dark:text-slate-300 font-medium">
                  {t('auth.email')}
                </Label>
                <Input
                  id="email"
                  type="email"
                  placeholder="name@company.com"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  required
                  className="h-11 bg-white dark:bg-slate-800 dark:bg-slate-900 border-slate-200 dark:border-slate-600 focus:border-slate-800 dark:focus:border-slate-400 focus:ring-2 focus:ring-slate-100 dark:focus:ring-slate-700"
                  data-testid="login-email-input"
                />
              </div>

              <div className="space-y-2">
                <div className="flex justify-between items-center">
                  <Label htmlFor="password" className="text-slate-700 dark:text-slate-300 font-medium">
                    {t('auth.password')}
                  </Label>
                  <Link 
                    to="/forgot-password"
                    className="text-sm text-slate-500 dark:text-slate-400 hover:text-slate-700 dark:hover:text-slate-200"
                  >
                    {t('auth.forgotPassword')}
                  </Link>
                </div>
                <div className="relative">
                  <Input
                    id="password"
                    type={showPassword ? 'text' : 'password'}
                    placeholder="••••••••"
                    value={formData.password}
                    onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                    required
                    className="h-11 bg-white dark:bg-slate-800 dark:bg-slate-900 border-slate-200 dark:border-slate-600 focus:border-slate-800 dark:focus:border-slate-400 focus:ring-2 focus:ring-slate-100 dark:focus:ring-slate-700 pr-10"
                    data-testid="login-password-input"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 dark:hover:text-slate-200"
                  >
                    {showPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                  </button>
                </div>
              </div>

              <Button
                type="submit"
                className="w-full h-11 bg-slate-900 dark:bg-slate-100 text-white dark:text-slate-900 hover:bg-slate-800 dark:hover:bg-slate-200 font-medium"
                disabled={isLoading}
                data-testid="login-submit-btn"
              >
                {isLoading ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    {t('common.loading')}
                  </>
                ) : (
                  t('common.login')
                )}
              </Button>
            </form>
          </div>
        </div>
      </div>

      {/* Right Side - Image */}
      <div className="hidden lg:flex flex-1 bg-slate-900 items-center justify-center p-12">
        <div className="max-w-lg text-center">
          <h2 
            className="text-3xl font-bold text-white mb-4"
            style={{ fontFamily: 'Manrope, sans-serif' }}
          >
            {t('login.heroTitle')}
          </h2>
          <p className="text-slate-400 text-lg">
            {t('login.heroDescription')}
          </p>
          <div className="mt-12">
            <img 
              src={settings.loginImage}
              alt="Team meeting"
              className="rounded-2xl shadow-2xl"
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default Login;
